/**
 * clientContext — the diagnostic snapshot attached to a bug report.
 *
 * A bug report that says "the grid broke" costs a round-trip to triage. This
 * module captures the reporter's situation for them, and above all captures
 * enough to go WATCH IT HAPPEN: this app runs Microsoft Clarity (the tag in
 * index.html, and the `clarity("identify", <email>)` call in
 * AppContextProvider.jsx), so a session id plus an accurate browser-side
 * timestamp turns a ticket into a session replay.
 *
 * WHY TWO TIMESTAMPS
 * The server stamps `created_at` when it accepts the ticket. That is the
 * authoritative ordering but it is up to a round-trip late, and it is the
 * SERVER's clock. Clarity's replay timeline is stamped by the BROWSER. So we
 * send `client_reported_at` — the browser's clock at the moment Submit was
 * pressed — plus the reporter's IANA zone and UTC offset, because the Clarity
 * dashboard renders times in the VIEWER's zone and an admin in Oklahoma
 * looking for a report from a broker in a different zone will otherwise
 * search the wrong hour. Keeping both lets the admin see clock skew instead
 * of being silently misled by it.
 *
 * Everything here is best-effort and non-throwing. A diagnostic block is a
 * nice-to-have; failing to collect one must never stop somebody filing a bug.
 * Every field is optional server-side and the whole block is treated as
 * untrusted there — it is breadcrumbs for a human, never an input to an
 * authorization decision.
 *
 * Pure-ish functions living in utils/ (not a hook) so they are testable
 * without a component, per the repo convention.
 */

// `__APP_VERSION__` is injected by Vite's `define` (see vite.config.js). The
// guard keeps this importable anywhere the define is absent.
const appVersion = () => {
  try {
    // eslint-disable-next-line no-undef
    return typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : null;
  } catch {
    return null;
  }
};

/**
 * Ask Clarity who this session is.
 *
 * `clarity("identify", ...)` returns a promise resolving to
 * `{ id, session, page, userHint }`. Three things can go wrong and all of
 * them are normal, not errors:
 *   - Clarity isn't on the page at all (VITE_CLARITY_PROJECT_ID unset in
 *     local dev, or an ad blocker ate the tag),
 *   - the call returns something that isn't a promise (older tag builds),
 *   - it never settles.
 * So we race it against a short timeout and resolve to nulls on any of them.
 *
 * @param {string} [userEmail] — passed through as the custom id, matching
 *   what AppContextProvider already identifies with, so the ids we get back
 *   belong to the same Clarity user the dashboard shows.
 * @param {number} [timeoutMs=1500]
 * @returns {Promise<{sessionId: string|null, pageId: string|null, userId: string|null}>}
 */
export const getClarityIds = async (userEmail, timeoutMs = 1500) => {
  const empty = { sessionId: null, pageId: null, userId: null };
  const clarity = typeof window !== "undefined" ? window.clarity : undefined;
  if (typeof clarity !== "function") return empty;

  try {
    const result = clarity("identify", userEmail);
    if (!result || typeof result.then !== "function") return empty;

    const timeout = new Promise((resolve) =>
      setTimeout(() => resolve(null), timeoutMs)
    );
    const settled = await Promise.race([result, timeout]);
    if (!settled) return empty;

    return {
      sessionId: settled.session ?? null,
      pageId: settled.page ?? null,
      userId: settled.id ?? null,
    };
  } catch {
    return empty;
  }
};

/**
 * Force Clarity to record this session.
 *
 * Clarity SAMPLES sessions — it does not keep every one. That is fine for
 * heatmaps and fatal for bug reports: the single session you most want to
 * watch is the one where something broke, and by default it is as likely to
 * be dropped as any other. `clarity("upgrade", <reason>)` marks the current
 * session as one to keep.
 *
 * Call this when the report form OPENS, not when it submits — the interesting
 * part of the recording is what the user did BEFORE they gave up and reached
 * for the form, and upgrading only covers the session it is called in.
 *
 * No-ops silently when Clarity isn't present.
 */
export const upgradeClaritySession = (reason = "feedback-report") => {
  try {
    if (typeof window !== "undefined" && typeof window.clarity === "function") {
      window.clarity("upgrade", reason);
    }
  } catch {
    // Telemetry must never break the form.
  }
};

/**
 * Build the Clarity dashboard URL for a recorded session.
 *
 * Clarity has no documented permalink to a single replay, so this lands the
 * admin on the project's recordings list filtered to the right day, and the
 * ticket shows the session id to match against. The format lives HERE, in one
 * frontend helper, rather than being composed server-side and frozen into a
 * database column — Microsoft can change their dashboard's URL shape, and
 * when they do this is a one-line frontend fix instead of a migration.
 *
 * @param {object} args
 * @param {string} [args.projectId] — defaults to VITE_CLARITY_PROJECT_ID
 * @param {string} [args.sessionId]
 * @param {string|Date} [args.at] — the moment to centre the search on
 * @returns {string|null} null when there is nothing useful to link to
 */
export const buildClarityReplayUrl = ({ projectId, sessionId, at } = {}) => {
  const project =
    projectId || import.meta.env?.VITE_CLARITY_PROJECT_ID || null;
  if (!project || !sessionId) return null;

  const params = new URLSearchParams();
  // Clarity's recordings list is date-filtered; anchoring on the report's own
  // day is the difference between one page of results and thousands.
  const when = at ? new Date(at) : new Date();
  if (!Number.isNaN(when.getTime())) {
    params.set("date", when.toISOString().slice(0, 10));
  }
  params.set("SessionId", sessionId);

  return `https://clarity.microsoft.com/projects/view/${encodeURIComponent(
    project
  )}/impressions?${params.toString()}`;
};

/**
 * The reporter's timezone, as an IANA name plus the UTC offset in minutes.
 *
 * The offset is normalized to the conventional sign (UTC-5 is -300), the
 * OPPOSITE of `Date.prototype.getTimezoneOffset()`, which returns +300 for
 * the same zone. Getting this backwards would put an admin an entire ten
 * hours off when they went looking in the replay, so it is flipped once here
 * rather than at each reading site.
 */
export const getTimezoneInfo = (now = new Date()) => {
  let timezone = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timezone = null;
  }
  let offset = null;
  try {
    offset = -now.getTimezoneOffset();
  } catch {
    offset = null;
  }
  return { timezone, offsetMinutes: offset };
};

/**
 * How far apart the reporter's browser clock and the server clock were, in
 * seconds (positive = the browser was ahead).
 *
 * This is why both stamps are stored. An admin searching Clarity by time is
 * searching the BROWSER's timeline; if the reporter's machine is minutes off,
 * looking at the server's `created_at` sends them to the wrong place with no
 * hint that anything is wrong. Returned as a number rather than rendered so
 * the caller decides what gap is worth flagging.
 *
 * Null when either stamp is missing or unparseable.
 */
export const clockSkewSeconds = (clientReportedAt, createdAt) => {
  if (!clientReportedAt || !createdAt) return null;
  const c = new Date(clientReportedAt).getTime();
  const s = new Date(createdAt).getTime();
  if (Number.isNaN(c) || Number.isNaN(s)) return null;
  return Math.round((c - s) / 1000);
};

/**
 * Capture the full diagnostic block to send with a feedback request.
 *
 * Shape matches the server's `FeedbackClientContext` exactly (snake_case) —
 * that model is `extra="forbid"`, so a renamed key here is a 422 rather than
 * diagnostics silently disappearing.
 *
 * @param {object} args
 * @param {string} [args.userEmail]  — for the Clarity identify call
 * @param {string} [args.route]      — react-router pathname, e.g. "/deals"
 * @param {Date|string} [args.occurredAt] — optional "it happened earlier"
 * @param {Date} [args.now]          — injectable for tests
 * @returns {Promise<object>} the `context` payload
 */
export const captureClientContext = async ({
  userEmail,
  route,
  occurredAt,
  now = new Date(),
} = {}) => {
  const { timezone, offsetMinutes } = getTimezoneInfo(now);
  const clarity = await getClarityIds(userEmail);

  const clientReportedAt = now.toISOString();

  return {
    // Browser clock. The server keeps its own `created_at` separately.
    client_reported_at: clientReportedAt,
    client_timezone: timezone,
    client_tz_offset_minutes: offsetMinutes,
    occurred_at: occurredAt ? new Date(occurredAt).toISOString() : null,

    clarity_session_id: clarity.sessionId,
    clarity_page_id: clarity.pageId,
    clarity_user_id: clarity.userId,
    clarity_replay_url: buildClarityReplayUrl({
      sessionId: clarity.sessionId,
      at: now,
    }),

    page_url: typeof window !== "undefined" ? window.location?.href ?? null : null,
    app_route: route ?? null,
    app_version: appVersion(),
    user_agent:
      typeof navigator !== "undefined" ? navigator.userAgent ?? null : null,
    viewport:
      typeof window !== "undefined" && window.innerWidth
        ? `${window.innerWidth}x${window.innerHeight}`
        : null,
  };
};

export default {
  captureClientContext,
  clockSkewSeconds,
  getClarityIds,
  getTimezoneInfo,
  buildClarityReplayUrl,
  upgradeClaritySession,
};
